import type { CheckoutResource } from '@/api/resources';

type PlainObject = Record<string, unknown>;

/** Upsell groups the cheap (include_upsells=0) responses leave out; loadUpsells() fetches them later. */
export const LAZY_UPSELL_GROUPS = ['treatments', 'hotel_rooms', 'lunch_and_diner', 'rituals'];

/** The combination the lazily loaded upsell groups are valid for. */
export const upsellsKey = (checkout: CheckoutResource): string =>
	[
		checkout.data?.arrival?.date,
		checkout.data?.departure?.date,
		checkout.data?.product_id,
		checkout.data?.persons_count,
		checkout.data?.amount_of_rooms,
	].join('|');

const isPlainObject = (value: unknown): value is PlainObject =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

function syncInPlace(target: PlainObject, source: PlainObject): void {
	Object.keys(target)
		.filter((key) => !(key in source))
		.forEach((key) => delete target[key]);

	Object.entries(source).forEach(([key, value]) => {
		const current = target[key];

		if (isPlainObject(value) && isPlainObject(current)) {
			syncInPlace(current, value);
		} else {
			target[key] = value;
		}
	});
}

/**
 * Unlike $patch, which deep merges and so keeps meta the server leaves out after a product switch (hotel
 * reservation costs, cancellation guarantee, ...), this drops it. It syncs in place, so components holding
 * a reference to the checkout keep updating. Lazily loaded upsell groups are kept only while the response
 * still matches the checkout and combination they were loaded for.
 */
export function syncCheckout(
	checkout: CheckoutResource,
	response: CheckoutResource,
	loadedUpsellsKey: string | null,
): void {
	const keepsLazyUpsells =
		checkout.data?.checkout_id === response.data.checkout_id && upsellsKey(response) === loadedUpsellsKey;

	const lazyUpsells = keepsLazyUpsells
		? Object.fromEntries(
				Object.entries(checkout.meta?.upsells ?? {}).filter(([group]) => LAZY_UPSELL_GROUPS.includes(group)),
			)
		: {};

	syncInPlace(checkout, {
		...response,
		meta: { ...response.meta, upsells: { ...lazyUpsells, ...response.meta.upsells } },
	});
}
